import { useState, type FormEvent } from "react";
import { api, ApiHatasi, type DnsKayit, type Musteri } from "../api";
import { Bildirim, Durum, HataKutusu, Kart, Rozet, Tablo, useCek } from "../ui";

const DURUM_ROZET: Record<string, "iyi" | "uyari" | "kotu"> = {
  tamam: "iyi",
  hatali: "uyari",
  eksik: "kotu",
};
const DURUM_YAZI: Record<string, string> = { tamam: "tamam", hatali: "hatalı", eksik: "eksik" };

/** Uzun DKIM anahtarlari tabloyu tasirmasin */
function Deger({ metin }: { metin: string }) {
  const [acik, setAcik] = useState(false);
  const uzun = metin.length > 90;
  return (
    <div className="tek" style={{ wordBreak: "break-all", maxWidth: 480 }}>
      {acik || !uzun ? metin : metin.slice(0, 90) + "…"}
      {uzun && (
        <button
          className="dugme kucuk"
          style={{ marginLeft: 8, verticalAlign: "middle" }}
          onClick={() => setAcik((v) => !v)}
        >
          {acik ? "kısalt" : "tamamı"}
        </button>
      )}
    </div>
  );
}

export function AlanAdiDetay({ id, git }: { id: string; git: (y: string) => void }) {
  const dns = useCek(() => api.alanDns(id), [id]);
  const [islem, setIslem] = useState<ApiHatasi | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState<string | null>(null);

  async function calistir(ad: string, f: () => Promise<string>) {
    setIslem(null);
    setBilgi(null);
    setBekliyor(ad);
    try {
      setBilgi(await f());
      dns.yenile();
    } catch (h) {
      setIslem(h instanceof ApiHatasi ? h : new ApiHatasi(String(h), "bilinmiyor", 0));
    } finally {
      setBekliyor(null);
    }
  }

  return (
    <Durum cekim={dns}>
      {(d) => (
        <>
          <div className="baslik">
            <div>
              <h1>{d.alanAdi}</h1>
              <p>
                {d.hazir
                  ? "Zorunlu kayıtların tümü doğru. Bu alan adından gönderim açık."
                  : `${d.eksikZorunlu} zorunlu kayıt eksik veya hatalı. Tamamlanana kadar bu alan adından gönderim kapalı.`}
              </p>
            </div>
            <div className="eylemler">
              <button className="dugme" onClick={() => git("/alanadlari")}>
                ← liste
              </button>
              <button className="dugme" onClick={() => api.yolHaritasiPdf(id, d.alanAdi ?? "alan-adi")}>
                Yol haritası (PDF)
              </button>
              <button
                className="dugme"
                disabled={bekliyor !== null}
                onClick={() =>
                  calistir("dogrula", async () => {
                    const y = await api.alanDogrula(id);
                    return y.mesaj;
                  })
                }
              >
                {bekliyor === "dogrula" ? "Doğrulanıyor…" : "DNS'i doğrula"}
              </button>
              <button
                className="dugme"
                disabled={bekliyor !== null}
                onClick={() => {
                  const sebep = window.prompt(
                    "Alan adını durdurma sebebi:\n\nKutular ve postalar yerinde kalır; " +
                      "yalnızca giriş ve gönderim durur.",
                    "Ödeme alınmadı",
                  );
                  if (sebep === null) return;
                  calistir("durdur", async () => (await api.alanDurdur(id, sebep)).mesaj);
                }}
              >
                {bekliyor === "durdur" ? "Durduruluyor…" : "Durdur"}
              </button>
              <button
                className="dugme"
                disabled={bekliyor !== null}
                onClick={() => calistir("baslat", async () => (await api.alanBaslat(id)).mesaj)}
              >
                {bekliyor === "baslat" ? "Açılıyor…" : "Yeniden aç"}
              </button>
              <button
                className="dugme tehlike"
                disabled={bekliyor !== null}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Alan adı silinsin mi?\n\nBu işlem geri alınamaz. İçinde posta kutusu varsa " +
                        "işlem reddedilir; önce kutuları silmeniz gerekir.",
                    )
                  )
                    return;
                  calistir("sil", async () => {
                    const y = await api.alanSil(id);
                    git("/alanadlari");
                    return y.mesaj;
                  });
                }}
              >
                Sil
              </button>
              {d.otomatikAyarlanabilir && (
                <button
                  className="dugme ana"
                  disabled={bekliyor !== null}
                  onClick={() =>
                    calistir("uygula", async () => {
                      const y = await api.alanDnsUygula(id);
                      const yazilan = y.sonuc.filter((s) => s.durum !== "degismedi");
                      return yazilan.length
                        ? `${yazilan.length} kayıt yazıldı: ` +
                            yazilan.map((s) => `${s.tur} ${s.ad}`).join(", ") +
                            ". Yayılmasını bekleyip doğrulayın."
                        : "Tüm kayıtlar zaten doğruydu, değişiklik yapılmadı.";
                    })
                  }
                >
                  {bekliyor === "uygula" ? "Yazılıyor…" : "Cloudflare ile ayarla"}
                </button>
              )}
            </div>
          </div>

          {islem && <HataKutusu hata={islem} />}
          {bilgi && <Bildirim tur="iyi">{bilgi}</Bildirim>}

          {!d.hazir && (
            <Bildirim tur="uyari">
              <strong>Gönderim kilidi:</strong> SPF ve DKIM'i olmayan bir alan adından çıkan posta hem spam'e
              düşer hem de sunucudaki diğer müşterilerin itibarını zedeler. Bu yüzden zorunlu kayıtlar
              tamamlanana kadar gönderim kapalı tutulur. <strong>Gelen posta kabul edilmeye devam eder.</strong>
            </Bildirim>
          )}

          {!d.dnsBizde && (
            <Bildirim>
              Bu alan adının DNS yönetimi bizde değil. Kayıtları kendi sağlayıcınızın panelinden girmeniz
              gerekiyor — yukarıdaki <strong>Yol haritası (PDF)</strong> belgesinde hepsi adım adım yazılı.
            </Bildirim>
          )}

          <KayitTablosu baslik="Zorunlu kayıtlar" kayitlar={d.kayitlar.filter((k) => k.zorunlu)} />
          <div style={{ height: 16 }} />
          <KayitTablosu baslik="Önerilen kayıtlar" kayitlar={d.kayitlar.filter((k) => !k.zorunlu)} />
        </>
      )}
    </Durum>
  );
}

function KayitTablosu({ baslik, kayitlar }: { baslik: string; kayitlar: DnsKayit[] }) {
  if (!kayitlar.length) return null;
  return (
    <Kart baslik={`${baslik} (${kayitlar.filter((k) => k.durum === "tamam").length}/${kayitlar.length} tamam)`}>
      <Tablo basliklar={["Durum", "Tür", "Ad", "Olması gereken / şu an"]}>
        {kayitlar.map((k) => (
          <tr key={k.tur + k.ad}>
            <td>
              <Rozet tur={DURUM_ROZET[k.durum]}>{DURUM_YAZI[k.durum] ?? k.durum}</Rozet>
            </td>
            <td className="tek">{k.tur}</td>
            <td className="tek" style={{ wordBreak: "break-all", maxWidth: 260 }}>
              {k.ad}
            </td>
            <td>
              <Deger metin={k.beklenen} />
              {k.canli && k.durum !== "tamam" && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ color: "var(--bad)", fontSize: 12 }}>şu an: </span>
                  <span className="tek" style={{ color: "var(--bad)", wordBreak: "break-all" }}>
                    {k.canli}
                  </span>
                </div>
              )}
              <div style={{ color: "var(--n-500)", fontSize: 12, marginTop: 4, maxWidth: 520 }}>{k.aciklama}</div>
              {k.tespit && (
                <div style={{ color: "var(--brand-deep)", fontSize: 12, marginTop: 3, maxWidth: 520, fontWeight: 600 }}>
                  {k.tespit}
                </div>
              )}
            </td>
          </tr>
        ))}
      </Tablo>
    </Kart>
  );
}

// --------------------------------------------------------------- sihirbaz

export function YeniAlanAdi({
  musteriler,
  bitti,
  vazgec,
}: {
  musteriler: Musteri[];
  bitti: (id: string) => void;
  vazgec: () => void;
}) {
  const [ad, setAd] = useState("");
  const [kiraciId, setKiraciId] = useState(musteriler[0]?.id ?? "");
  const [hata, setHata] = useState<ApiHatasi | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ id: string; mesaj: string; dnsBizde: boolean; hazir: boolean } | null>(null);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setHata(null);
    setBekliyor(true);
    try {
      const y = await api.alanAc({ ad: ad.trim(), kiraciId: kiraciId || undefined });
      setSonuc({ id: y.veri.id, mesaj: y.mesaj, dnsBizde: y.dnsBizde, hazir: y.hazir });
    } catch (h) {
      setHata(h instanceof ApiHatasi ? h : new ApiHatasi(String(h), "bilinmiyor", 0));
    } finally {
      setBekliyor(false);
    }
  }

  if (sonuc) {
    return (
      <Kart baslik="Alan adı eklendi">
        <div className="ic">
          <Bildirim tur={sonuc.hazir ? "iyi" : "uyari"}>{sonuc.mesaj}</Bildirim>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="dugme ana" onClick={() => bitti(sonuc.id)}>
              DNS durumunu aç
            </button>
            <button className="dugme" onClick={() => api.yolHaritasiPdf(sonuc.id, "alan-adi")}>
              Yol haritası (PDF)
            </button>
          </div>
        </div>
      </Kart>
    );
  }

  return (
    <Kart baslik="Yeni alan adı">
      <form className="ic" onSubmit={gonder}>
        {hata && <HataKutusu hata={hata} />}
        <div className="izgara d2">
          <label className="alan">
            <span>Alan adı *</span>
            <input
              value={ad}
              onChange={(e) => setAd(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))}
              placeholder="kurumadi.com"
              required
              autoFocus
            />
            <small>Başında www ya da https:// olmadan yazın.</small>
          </label>
          <label className="alan">
            <span>Müşteri</span>
            <select value={kiraciId} onChange={(e) => setKiraciId(e.target.value)}>
              <option value="">— kiracıya bağlama —</option>
              {musteriler.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.ad} ({m.paket})
                </option>
              ))}
            </select>
            <small>Kiracıya bağlanmayan alan adı hiçbir müşterinin limitine sayılmaz.</small>
          </label>
        </div>
        <Bildirim>
          DNS yönetimi bizdeyse kayıtlar <strong>otomatik yazılır</strong>. Değilse müşteriye verilecek
          PDF yol haritası üretilir. DKIM anahtarları sunucu tarafından oluşturulur.
        </Bildirim>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="dugme ana" disabled={bekliyor}>
            {bekliyor ? "Ekleniyor…" : "Alan adını ekle"}
          </button>
          <button type="button" className="dugme" onClick={vazgec}>
            Vazgeç
          </button>
        </div>
      </form>
    </Kart>
  );
}
